'use client';

import { useLenis } from 'lenis/react';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

export const FEATURE_CATEGORIES = [
  { id: 'facturation', label: 'Facturation & Finance' },
  { id: 'gestion', label: 'Gestion & Pilotage' },
  { id: 'documents', label: 'Documents & Dossiers' },
  { id: 'innovation', label: 'Innovation' },
] as const;

export type FeatureCategoryId = (typeof FEATURE_CATEGORIES)[number]['id'];

type FeaturesCategoryContextValue = {
  activeCategory: FeatureCategoryId;
  setActiveCategory: (category: FeatureCategoryId) => void;
  goToFacturationElectronique: () => void;
  goToCategory: (id: FeatureCategoryId) => void;
};

const FACTURATION_ELECTRONIQUE_ID = 'facturation-electronique';

const FeaturesCategoryContext = createContext<FeaturesCategoryContextValue | null>(null);

export const FeaturesCategoryProvider = ({ children }: { children: ReactNode }) => {
  const [activeCategory, setActiveCategory] = useState<FeatureCategoryId>('facturation');
  const lenis = useLenis();

  const scrollToFacturationElectronique = useCallback(() => {
    const element = document.getElementById(FACTURATION_ELECTRONIQUE_ID);
    if (!element) return false;

    const targetY = element.getBoundingClientRect().top + window.scrollY - 96;

    if (lenis) {
      lenis.scrollTo(targetY, { immediate: false });
    } else {
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }
    return true;
  }, [lenis]);

  const goToCategory = useCallback(
    (id: FeatureCategoryId) => {
      setActiveCategory(id);
      const section = document.getElementById('fonctionnalites');
      if (!section) return;
      const targetY = section.getBoundingClientRect().top + window.scrollY - 96;
      if (lenis) {
        lenis.scrollTo(targetY, { immediate: false });
      } else {
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    },
    [lenis, setActiveCategory],
  );

  const goToFacturationElectronique = useCallback(() => {
    if (activeCategory !== 'facturation') {
      setActiveCategory('facturation');
    }

    let attempts = 8;

    const scheduleScroll = () => {
      requestAnimationFrame(() => {
        if (scrollToFacturationElectronique()) return;
        attempts -= 1;
        if (attempts > 0) {
          scheduleScroll();
        }
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scheduleScroll);
    });
  }, [activeCategory, scrollToFacturationElectronique]);

  const value = useMemo(
    () => ({
      activeCategory,
      goToCategory,
      goToFacturationElectronique,
      setActiveCategory,
    }),
    [activeCategory, goToCategory, goToFacturationElectronique],
  );

  return (
    <FeaturesCategoryContext.Provider value={value}>{children}</FeaturesCategoryContext.Provider>
  );
};

export const useFeaturesCategory = () => {
  const context = useContext(FeaturesCategoryContext);

  if (!context) {
    throw new Error('useFeaturesCategory must be used within a FeaturesCategoryProvider');
  }

  return context;
};
